export const DEFAULT_ORDER_NUMBER_TEMPLATE = "MG-{seq:8}";

export const ORDER_NUMBER_TEMPLATE_PRESETS = [
  { label: "MG + 8-digit seq", template: "MG-{seq:8}" },
  { label: "Daily MG", template: "MG-{YYYY}{MM}{DD}-{seq:4}" },
  { label: "Monthly ORD", template: "ORD-{YY}{MM}-{seq:6}" },
] as const;

export const ORDER_NUMBER_TEMPLATE_TOKENS = [
  { token: "{YYYY}", description: "4-digit year (2026)" },
  { token: "{YY}", description: "2-digit year (26)" },
  { token: "{MM}", description: "2-digit month (08)" },
  { token: "{DD}", description: "2-digit day (11)" },
  { token: "{seq}", description: "Sequence number (unpadded)" },
  { token: "{seq:N}", description: "Zero-padded sequence (e.g. {seq:4} → 0042)" },
] as const;

const SEQ_TOKEN = /\{seq(?::(\d+))?\}/g;

export type OrderNumberTemplateError = {
  ok: false;
  error: string;
};

export type OrderNumberTemplateOk = {
  ok: true;
  hasSeqPadding: boolean;
  paddingWidth: number | null;
};

export function parseOrderNumberTemplate(template: string): OrderNumberTemplateOk | OrderNumberTemplateError {
  const trimmed = template.trim();
  if (!trimmed) {
    return { ok: false, error: "Template is required" };
  }

  const matches = [...trimmed.matchAll(SEQ_TOKEN)];
  if (matches.length === 0) {
    return { ok: false, error: "Template must include {seq} or {seq:N}" };
  }
  if (matches.length > 1) {
    return { ok: false, error: "Template may only include one sequence token" };
  }

  const paddingRaw = matches[0][1];
  if (paddingRaw != null) {
    const width = Number.parseInt(paddingRaw, 10);
    if (!Number.isFinite(width) || width < 1 || width > 12) {
      return { ok: false, error: "Sequence padding must be between 1 and 12 digits" };
    }
    return { ok: true, hasSeqPadding: true, paddingWidth: width };
  }

  return { ok: true, hasSeqPadding: false, paddingWidth: null };
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Scope key for the incremental counter (daily / monthly / yearly / global). */
export function orderNumberScopeKey(template: string, now = new Date()): string {
  const hasYear = template.includes("{YYYY}") || template.includes("{YY}");
  const hasMonth = template.includes("{MM}");
  const hasDay = template.includes("{DD}");

  if (hasYear && hasMonth && hasDay) {
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  }
  if (hasYear && hasMonth) {
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
  }
  if (hasYear) {
    return String(now.getFullYear());
  }
  return "global";
}

function formatSeq(seq: number, paddingWidth: number | null): string {
  if (paddingWidth != null) {
    return String(seq).padStart(paddingWidth, "0");
  }
  return String(seq);
}

/** Render an order number from template, sequence value, and reference date. */
export function formatOrderNumber(template: string, seq: number, now = new Date()): string {
  const parsed = parseOrderNumberTemplate(template);
  if (!parsed.ok) {
    return formatOrderNumber(DEFAULT_ORDER_NUMBER_TEMPLATE, seq, now);
  }

  const yyyy = String(now.getFullYear());
  const yy = yyyy.slice(-2);
  const mm = pad2(now.getMonth() + 1);
  const dd = pad2(now.getDate());

  let output = template
    .replace(/\{YYYY\}/g, yyyy)
    .replace(/\{YY\}/g, yy)
    .replace(/\{MM\}/g, mm)
    .replace(/\{DD\}/g, dd);

  output = output.replace(SEQ_TOKEN, () => formatSeq(seq, parsed.paddingWidth));
  return output.toUpperCase();
}

/** Preview the next order number without consuming the sequence. */
export function previewOrderNumber(template: string, lastValue: number, now = new Date()): string {
  return formatOrderNumber(template, lastValue + 1, now);
}
